/**
 * Bulk printing from the participants tab:
 *   - Formularios de registro (letter, one per page) — signed at the desk,
 *     optionally followed by the receipt(s) of the participant's payments
 *   - Stickers de credencial (thermal labels)
 * Default selection = VERIFIED participants, sorted district → name.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { supabase } from '@/supabase.config';
import { usePermissions } from '@/hooks/usePermissions';
import { getEventPayments, type EventPayment } from '@/Services/eventPayments.service';
import { getReceiptsByIds } from '@/features/receipts/data/receiptsRepository';
import { LetterReceiptPrint } from '@/features/receipts/components/LetterReceiptPrint';
import { receiptToPrintData, type ReceiptPrintData } from '@/features/receipts/types';
import { publicReceiptUrl } from '@/features/receipts/utils/publicReceiptUrl';
import { IssueEventReceiptPrompt } from '../components/IssueEventReceiptPrompt';
import type { Event } from '@/types/event.types';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';
import { RegistrationFormPrint } from '../public/components/RegistrationFormPrint';
import { useSignatureImage } from '../public/components/useSignatureImage';
import type { PublicRegistration, RegistrationKind } from '../public/types';
import { PrintPortal } from './PrintPortal';
import { BadgeStickerPrint, type BadgeStickerData } from './BadgeStickers';
import { badgeQrUrl } from './badgeQrUrl';
import { participantToSticker } from './participantSticker';
import { useEventHasVoting } from '../hooks/useEventHasVoting';

type PrintKind = 'forms' | 'stickers';
type Selection = 'verified' | 'filtered';

function participantName(p: ParticipantWithDetails) {
  return p.minister?.full_name ?? p.guest?.full_name ?? '';
}

function sortForPrint(list: ParticipantWithDetails[]) {
  return [...list].sort((a, b) => {
    const da = a.minister?.district?.name ?? 'zzz';
    const db = b.minister?.district?.name ?? 'zzz';
    return da.localeCompare(db, 'es') || participantName(a).localeCompare(participantName(b), 'es');
  });
}

function FormWithSignature({ p, event, degreeNames }: { p: ParticipantWithDetails; event: Event; degreeNames: Map<string, string> }) {
  const signatureUrl = useSignatureImage(p.public_token, !!p.form_signed_at);
  return <RegistrationFormPrint registration={toPrintable(p, event, degreeNames)} qrUrl={badgeQrUrl(p.public_token)} signatureUrl={signatureUrl} />;
}

/** Admin-side equivalent of get_public_registration (no extra round trips) */
function toPrintable(p: ParticipantWithDetails, event: Event, degreeNames: Map<string, string>): PublicRegistration {
  const data = (p.registration_data ?? {}) as Record<string, string | undefined>;
  const kind = (data.kind as RegistrationKind) ?? (p.minister_id ? 'MINISTER' : p.guest?.is_delegate ? 'DELEGATE' : 'GUEST');
  const m = p.minister;
  return {
    event: {
      code: event.code,
      name: event.name,
      location: event.location,
      start_date: event.start_date,
      end_date: event.end_date,
      registration_form_type: event.registration_form_type === 'FULL_CONVENTION' ? 'FULL_CONVENTION' : 'SIMPLE',
      signature_mode: event.signature_mode === 'DIGITAL' ? 'DIGITAL' : 'ON_SITE',
      registration_declaration: event.registration_declaration ?? null,
    },
    registration_status: p.registration_status,
    payment_status: p.payment_status,
    attendance_status: p.attendance_status,
    calculated_price: p.calculated_price,
    registered_at: p.registration_date,
    declaration_accepted_at: p.declaration_accepted_at,
    form_signed_at: p.form_signed_at,
    kind,
    form: {
      ...data,
      full_name: data.full_name ?? participantName(p),
      dni: data.dni ?? p.guest?.dni ?? undefined,
      adb_code: m?.adb_code,
      district_name: m?.district?.name ?? undefined,
      region_name: m?.region?.name ?? undefined,
      degree_name: degreeNames.get(data.degree_id ?? m?.degree_id ?? '') ?? undefined,
      position: data.position ?? m?.position ?? undefined,
      cell_phone: data.cell_phone ?? p.guest?.phone ?? undefined,
      email: data.email ?? p.guest?.email ?? undefined,
      represented_name: p.guest?.representing_minister?.full_name ?? p.guest?.represented_name ?? undefined,
      represented_adb_code: p.guest?.representing_minister?.adb_code,
      institution_note: p.guest?.institution_note ?? undefined,
    },
  };
}

interface BulkPrintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: Event;
  /** Currently filtered participants in the tab */
  participants: ParticipantWithDetails[];
}

export function BulkPrintDialog({ open, onOpenChange, event, participants }: BulkPrintDialogProps) {
  const hasVoting = useEventHasVoting(event.id);
  const [kind, setKind] = useState<PrintKind>('forms');
  const [selection, setSelection] = useState<Selection>('verified');
  const [printing, setPrinting] = useState<PrintKind | null>(null);
  const [includeReceipts, setIncludeReceipts] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [receiptsByParticipant, setReceiptsByParticipant] = useState<Map<string, ReceiptPrintData[]>>(new Map());
  const [issueTarget, setIssueTarget] = useState<{ payment: EventPayment; participant: ParticipantWithDetails } | null>(null);
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canViewPayments = can('event_payment.view');
  const canIssueReceipts = can('event_payment.record');

  const { data: degreeNames = new Map<string, string>() } = useQuery({
    queryKey: ['degrees_names'],
    queryFn: async () => {
      const { data, error } = await supabase.from('degrees').select('id, name');
      if (error) throw new Error(error.message);
      return new Map((data ?? []).map((d) => [d.id, d.name as string]));
    },
    enabled: open || printing !== null,
    staleTime: 30 * 60 * 1000,
  });

  const selected = useMemo(
    () =>
      sortForPrint(
        selection === 'verified' ? participants.filter((p) => p.registration_status === 'VERIFIED') : participants,
      ),
    [participants, selection],
  );

  // Recorded (non-reversed) payments, to find each participant's receipts and the ones still missing
  const { data: payments = [] } = useQuery({
    queryKey: ['event_payments', 'bulk-print', event.id],
    queryFn: () => getEventPayments(event.id),
    enabled: open && canViewPayments,
  });

  const { receiptIdsByParticipant, missingPayments } = useMemo(() => {
    const ids = new Map<string, string[]>();
    const missing: EventPayment[] = [];
    const selectedIds = new Set(selected.map((p) => p.id));
    for (const pay of payments) {
      if (pay.status !== 'RECORDED' || !selectedIds.has(pay.participant_id)) continue;
      if (pay.receipt_id && pay.receipt?.status === 'issued') {
        ids.set(pay.participant_id, [...(ids.get(pay.participant_id) ?? []), pay.receipt_id]);
      } else if (!pay.receipt_id) {
        missing.push(pay);
      }
    }
    return { receiptIdsByParticipant: ids, missingPayments: missing };
  }, [payments, selected]);

  const receiptCount = [...receiptIdsByParticipant.values()].reduce((n, list) => n + list.length, 0);

  const start = async () => {
    if (kind === 'forms' && includeReceipts && receiptCount > 0) {
      setPreparing(true);
      try {
        const all = [...receiptIdsByParticipant.values()].flat();
        const receipts = await getReceiptsByIds(all);
        const byId = new Map(receipts.map((r) => [r.id, r]));
        const map = new Map<string, ReceiptPrintData[]>();
        for (const [participantId, list] of receiptIdsByParticipant) {
          const data = list
            .map((id) => byId.get(id))
            .filter((r): r is NonNullable<typeof r> => !!r && r.status === 'issued')
            .sort((a, b) => a.issued_at.localeCompare(b.issued_at))
            .map((r) => receiptToPrintData(r, publicReceiptUrl(r.public_token)));
          map.set(participantId, data);
        }
        setReceiptsByParticipant(map);
      } finally {
        setPreparing(false);
      }
    } else {
      setReceiptsByParticipant(new Map());
    }
    onOpenChange(false);
    setPrinting(kind);
  };

  const issueNext = () => {
    const payment = missingPayments[0];
    const participant = payment && selected.find((p) => p.id === payment.participant_id);
    if (payment && participant) setIssueTarget({ payment, participant });
  };

  if (printing === 'stickers') {
    const stickers: BadgeStickerData[] = selected.map((p) => participantToSticker(p, hasVoting));
    return <BadgeStickerPrint stickers={stickers} onClose={() => setPrinting(null)} />;
  }

  if (printing === 'forms') {
    return (
      <PrintPortal
        title={`Formularios de registro (${selected.length})`}
        pageSize="letter"
        onClose={() => setPrinting(null)}
      >
        {selected.flatMap((p) => [
          <div key={p.id} data-print-page className="shadow-lg">
            <FormWithSignature p={p} event={event} degreeNames={degreeNames} />
          </div>,
          ...(receiptsByParticipant.get(p.id) ?? []).map((r) => (
            <div key={`${p.id}-r${r.number}`} data-print-page className="shadow-lg">
              <LetterReceiptPrint data={r} />
            </div>
          )),
        ])}
      </PrintPortal>
    );
  }

  return (
    <>
    <Dialog open={open && !issueTarget} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Impresión masiva</DialogTitle>
          <DialogDescription>Ordenado por distrito y nombre.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Qué imprimir</Label>
            <SegmentedControl
              className="w-full"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'forms', label: 'Formularios' },
                { value: 'stickers', label: 'Stickers credencial' },
              ]}
            />
          </div>
          <div className="space-y-2">
            <Label>Participantes</Label>
            <SegmentedControl
              className="w-full"
              value={selection}
              onChange={setSelection}
              options={[
                { value: 'verified', label: 'Solo verificados' },
                { value: 'filtered', label: 'Todos los filtrados' },
              ]}
            />
          </div>
          <p className="text-sm text-muted-foreground">{selected.length} participante(s) seleccionados.</p>

          {kind === 'forms' && canViewPayments && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="include-receipts"
                  checked={includeReceipts}
                  onCheckedChange={(v) => setIncludeReceipts(v === true)}
                  disabled={receiptCount === 0}
                />
                <Label htmlFor="include-receipts" className="text-sm leading-snug">
                  Incluir el recibo después de cada formulario
                  <span className="block text-xs font-normal text-muted-foreground">
                    {receiptCount} recibo(s) emitido(s) · hoja carta
                  </span>
                </Label>
              </div>
              {missingPayments.length > 0 && (
                <div className="flex items-start gap-2 rounded bg-amber-50 p-2 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                  <div className="space-y-1">
                    <p>
                      {missingPayments.length} pago(s) sin recibo: esos formularios se imprimirán sin hoja de recibo.
                    </p>
                    {canIssueReceipts && (
                      <Button type="button" size="sm" variant="outline" className="h-7" onClick={issueNext}>
                        Emitir recibo pendiente
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={start} disabled={selected.length === 0 || preparing}>
            {preparing && <Loader2 className="mr-2 size-4 animate-spin" />}
            Vista previa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {issueTarget && (
      <IssueEventReceiptPrompt
        eventName={event.name}
        paymentId={issueTarget.payment.id}
        participant={issueTarget.participant}
        amount={Number(issueTarget.payment.amount)}
        method={issueTarget.payment.method ?? 'cash'}
        skipPrompt
        onClose={() => {
          setIssueTarget(null);
          queryClient.invalidateQueries({ queryKey: ['event_payments'] });
        }}
      />
    )}
    </>
  );
}
